import React, { useState, useRef, useEffect, useCallback } from "react";
import "./InvoiceModal.css";

// components imports

import StaticInvoice from "./Components/StaticInvoice";
import EditableInvoice from "./Components/EditableInvoice";
import PrintableInvoice from "./Components/PrintableInvoice";
// apis imports
import {
  fetchShipmentDataById,
  fetchAdminShipmentDataById,
} from "../../services/ShipperServices/Shipments/shipments.services";
// library imports
import Backdrop from "@mui/material/Backdrop";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import html2pdf from "html2pdf.js";
import { pdfjs } from "react-pdf";
import { useTranslation } from "react-i18next";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

export default function InvoiceModal({
  open,
  setOpen,
  data,
  type,
  id,
  setData,
  setShipments,
  getAllShipments,
  actions,
  setSnack,
}) {
  const { t } = useTranslation("finances");
  const handleClose = () => setOpen(false);
  const progressRef = useRef(null);
  const invoiceRef = useRef(null);

  const downloadPdf = async (data) => {
    const input = invoiceRef.current;
    const options = {
      filename: `${
        data.tattension + " " + data.shipment_tracking_id
      } ${t("invoice.buttons.invoice")}.pdf`,
      format: "A4",
      jsPDF: { unit: "px", format: [830, 1000], orientation: "p" },
      image: { type: "jpeg", quality: 0.98 },
      pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      html2canvas: { scale: 2, useCORS: true },
    };

    await html2pdf().set(options).from(input).save();
  };

  const downloadDocs = async (data, type) => {
    const DOWNLOAD_CONFIG = {
      label: {
        fileKey: "single_shipping_labels",
        label: t("invoice.buttons.label"),
      },
      declaration: {
        fileKey: "single_shipping_invoice",
        label: t("invoice.buttons.declaration"),
      },
    };

    const config = DOWNLOAD_CONFIG[type];

    if (!config) {
      setSnack((snack) => ({
        ...snack,
        open: true,
        text: t("invoice.invalidDownloadType"),
        severity: "error",
      }));
      return;
    }

    const { fileKey, label } = config;
    // const loadingKey = `${type}${data?.id}`;

    // setLabelDownloading(loadingKey); // no loader available currently

    try {
      const fileUrl = data?.[fileKey]?.file;

      if (!fileUrl) {
        throw new Error("File URL missing");
      }

      const response = await fetch(fileUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/pdf",
        },
      });

      if (!response.ok) {
        throw new Error(`Failed with status ${response.status}`);
      }

      const blob = await response.blob();

      if (!blob || blob.size === 0) {
        throw new Error("Empty file");
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `${data?.tattension} ${data?.shipment_tracking_id} ${label}.pdf`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(`[${label} Download Error]`, error);

      setSnack((snack) => ({
        ...snack,
        open: true,
        text: t("common:messages.somethingWentWrong"),
        severity: "error",
      }));
    } finally {
      // setLabelDownloading(""); // no loader available currently
    }
  };

  const [shipmentData, setShipmentData] = useState(null);
  const [loading, setLoading] = useState(true);

  const getShipmentDataById = useCallback(
    async (id) => {
      setLoading(true);
      let response;
      if (type === "editable") {
        response = await fetchAdminShipmentDataById(id);
      } else {
        response = await fetchShipmentDataById(id);
      }
      if (!response.error) {
        setLoading(false);
        const rawResult = response?.data?.result;
        const { validCharges, removedCharges, quoteCharges, estCharges } = (
          rawResult?.shipment_charges || []
        ).reduce(
          (acc, charge) => {
            if (!charge?.invoice_id) {
              if (charge?.is_for_user === "0") {
                acc.quoteCharges.push(charge);
              } else if (charge?.is_for_user === "2" && type === "editable") {
                acc.estCharges.push(charge);
              } else {
                acc.validCharges.push(charge);
              }
            } else {
              acc.removedCharges.push(charge);
            }

            return acc;
          },
          {
            validCharges: [],
            removedCharges: [],
            quoteCharges: [],
            estCharges: [],
          },
        );

        // Create the new result object
        const finalResult = {
          ...rawResult,
          shipment_charges: validCharges,
          billingAutomation: removedCharges,
          quoteCharges: quoteCharges,
          estCharges: estCharges,
        };
        setShipmentData(finalResult);
      } else {
        setLoading(false);
        setSnack((snack) => {
          return {
            ...snack,
            open: true,
            text: t("common:messages.somethingWentWrong"),
            severity: "error",
          };
        });
      }
    },
    [type, setSnack, t],
  );

  const refreshShipmentData = useCallback(
    (shipmentId) => {
      if (shipmentId) {
        getShipmentDataById(shipmentId);
      }
    },
    [getShipmentDataById],
  );

  useEffect(() => {
    if (data) {
      getShipmentDataById(data);
    }
  }, [data, getShipmentDataById]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Fade in={open}>
        <div className="invoices__modal_wrapper">
          {(!type || type === "static") && (
            <StaticInvoice
              data={shipmentData}
              handleClose={handleClose}
              downloadPdf={downloadPdf}
              downloadDocs={downloadDocs}
              progress={progressRef.current}
              setData={setShipmentData}
              setShipments={setShipments}
              getAllShipments={getAllShipments}
              actions={actions}
              setSnack={setSnack}
              loading={loading}
              refreshShipmentData={refreshShipmentData}
            />
          )}
          {type === "editable" && (
            <EditableInvoice
              data={shipmentData}
              handleClose={handleClose}
              downloadPdf={downloadPdf}
              downloadDocs={downloadDocs}
              id={id}
              setData={setShipmentData}
              setShipments={setShipments}
              getAllShipments={getAllShipments}
              type="admin"
              loading={loading}
              refreshShipmentData={refreshShipmentData}
              setSnack={setSnack}
            />
          )}
          <div
            style={{
              zIndex: "-1000",
              position: "absolute",
              visibility: "hidden",
              left: "-1000",
            }}
          >
            <PrintableInvoice invoiceRef={invoiceRef} data={shipmentData} />
          </div>
        </div>
      </Fade>
    </Modal>
  );
}
